import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
  CognitoUserAttribute,
  ICognitoUserPoolData,
  ISignUpResult,
} from 'amazon-cognito-identity-js';
import { config } from '../config';

const poolData: ICognitoUserPoolData = {
  UserPoolId: config.userPoolId,
  ClientId: config.userPoolClientId,
};

export function userPool(): CognitoUserPool {
  return new CognitoUserPool(poolData);
}

export interface LoginResult {
  session: CognitoUserSession;
}

export async function signIn(email: string, password: string): Promise<LoginResult> {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: userPool() });
    const authDetails = new AuthenticationDetails({ Username: email, Password: password });
    user.authenticateUser(authDetails, {
      onSuccess: (session) => resolve({ session }),
      onFailure: (err) => reject(err),
    });
  });
}

export interface SignUpInput {
  email: string;
  password: string;
  addressId: string;
}

/**
 * Sign up a new customer. The pre-signup Lambda reads `addressId` from
 * clientMetadata to run the first-come sentinel PUT.
 */
export async function signUp(input: SignUpInput): Promise<ISignUpResult> {
  const attrs: CognitoUserAttribute[] = [
    new CognitoUserAttribute({ Name: 'email', Value: input.email }),
  ];
  return new Promise((resolve, reject) => {
    userPool().signUp(
      input.email,
      input.password,
      attrs,
      [],
      (err, result) => {
        if (err || !result) return reject(err);
        resolve(result);
      },
      { addressId: input.addressId },
    );
  });
}

export async function confirmSignUp(
  email: string,
  code: string,
  clientMetadata?: Record<string, string>,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: userPool() });
    user.confirmRegistration(
      code,
      true,
      (err) => {
        if (err) return reject(err);
        resolve();
      },
      clientMetadata,
    );
  });
}

export async function resendConfirmationCode(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: userPool() });
    user.resendConfirmationCode((err) => {
      if (err) return reject(err);
      resolve();
    });
  });
}

export async function forgotPassword(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: userPool() });
    user.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}

export async function confirmForgotPassword(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: userPool() });
    user.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}

export function getCurrentSession(): Promise<CognitoUserSession | null> {
  return new Promise((resolve) => {
    const user = userPool().getCurrentUser();
    if (!user) return resolve(null);
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) return resolve(null);
      resolve(session);
    });
  });
}

export function signOut(): void {
  const user = userPool().getCurrentUser();
  user?.signOut();
}

export function idToken(session: CognitoUserSession): string {
  return session.getIdToken().getJwtToken();
}

export interface Claims {
  sub: string;
  username: string;
  email: string;
  role: 'customer' | 'staff' | 'admin';
}

export function claimsFromSession(session: CognitoUserSession): Claims {
  const payload = session.getIdToken().decodePayload();
  return {
    sub: String(payload.sub ?? ''),
    username: String(payload['cognito:username'] ?? payload.username ?? ''),
    email: String(payload.email ?? ''),
    role: (payload['custom:role'] ?? 'customer') as Claims['role'],
  };
}
